import React, { useState, useEffect } from 'react';
import { useApi } from '../../../hooks/useApi';
import API from '../../../services/api';

const WaiverSigning = ({ bookingData, onComplete, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [fullName, setFullName] = useState('');
  
  const { data: waiver, loading: waiverLoading } = useApi(() => API.content.getWaiverContent());

  const participants = Array.isArray(bookingData.participants) ? bookingData.participants : [];
  
  const riders = participants.filter(p => 
    p.is_rider === 1 || p.is_rider === '1' || p.is_rider === true ||
    (!p.is_passenger && !p.is_rider)
  );
  
  const firstParticipant = riders[0] || participants[0];
  const otherParticipants = riders.slice(1);

  useEffect(() => {
    if (firstParticipant?.full_name) {
      setFullName(firstParticipant.full_name);
    } else if (bookingData.customerInfo?.name) {
      setFullName(bookingData.customerInfo.name);
    }
  }, [firstParticipant, bookingData.customerInfo]);

  const handleSignWaiver = async (e) => {
    e.preventDefault();

    if (!agreedToTerms) {
      setError('You must agree to the terms and conditions');
      return;
    }

    if (!fullName.trim()) {
      setError('Please enter your full name');
      return;
    }

    const expectedName = firstParticipant?.full_name || bookingData.customerInfo?.name;
    if (!expectedName) {
      setError('Unable to verify participant name. Please go back and re-enter details.');
      return;
    }

    if (fullName.trim().toLowerCase() !== expectedName.trim().toLowerCase()) {
      setError(`Name must match exactly: ${expectedName}`);
      return;
    }

    if (!bookingData.reference) {
      setError('Booking reference is missing. Please go back and try again.');
      return;
    }

    if (!firstParticipant?.id) {
      setError('Participant ID is missing. Please go back and re-submit the form.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await API.bookings.signWaiver({
        booking_reference: bookingData.reference,
        participant_id: firstParticipant.id,
        full_name: fullName.trim()
      });

      if (response.success) {
        onComplete({ 
          waiversProcessed: true,
          firstParticipantSigned: true,
          hasOtherParticipants: otherParticipants.length > 0
        });
      } else {
        setError(response.message || 'Failed to sign waiver');
      }
    } catch (err) {
      setError(err.message || 'Failed to sign waiver');
    } finally {
      setLoading(false);
    }
  };

  if (!participants || participants.length === 0) {
    return (
      <div className="bg-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
        <div className="bg-red-500/20 border border-red-500 rounded-lg p-6 text-center">
          <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-xl font-bold text-red-400 mb-2">No Participant Data</h3>
          <p className="text-red-300 mb-4">
            Unable to load participant information. Please go back and resubmit the form.
          </p>
          <button
            onClick={onBack}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-bold transition-colors"
          >
            ← Go Back & Resubmit
          </button>
        </div>
      </div>
    );
  }

  if (!firstParticipant) {
    return (
      <div className="bg-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
        <div className="bg-red-500/20 border border-red-500 rounded-lg p-6 text-center">
          <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-xl font-bold text-red-400 mb-2">Participant Not Found</h3>
          <p className="text-red-300 mb-4">
            Unable to identify the lead rider. Please go back and resubmit the form.
          </p>
          <button
            onClick={onBack}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-bold transition-colors"
          >
            ← Go Back & Resubmit
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-playfair font-bold text-white mb-2">
          Sign Liability Waiver
        </h2>
        <p className="text-gray-400">
          Please review and sign the waiver to continue with your booking
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-200">
          {error}
        </div>
      )}


      {/* First Participant Info */}
      <div className="bg-gray-700 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold text-xl">
            {firstParticipant?.full_name?.charAt(0) || 'P'}
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Your Information</h3>
            <p className="text-gray-300">{firstParticipant?.full_name}</p>
            <p className="text-gray-500 text-sm">{firstParticipant?.email}</p>
          </div>
        </div>
      </div>

      {/* Other Participants Info - If Any */}
      {otherParticipants.length > 0 && (
        <div className="bg-blue-600/20 border border-blue-500 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-blue-200">
              <p className="font-bold mb-2">📧 Other Participants Will Sign Later</p>
              <p className="text-sm text-blue-300 mb-3">
                After payment is complete, the following participants will receive an email with a link to sign their waivers:
              </p>
              <ul className="space-y-2">
                {otherParticipants.map((participant, idx) => (
                  <li key={participant.id} className="flex items-center gap-2 text-sm">
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">
                      {idx + 2}
                    </div>
                    <span className="font-medium">{participant.full_name}</span>
                    <span className="text-blue-400">({participant.email})</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

{/* Waiver Content - Scrollable */}
      <div className="bg-white rounded-xl p-6 max-h-96 overflow-y-auto mb-6 shadow-lg">
        {waiverLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading waiver content...</p>
          </div>
        ) : waiver ? (
          <>
            <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4 text-center">
              {waiver.title}
            </h2>
            <p className="text-sm text-gray-600 mb-6 text-center">
              Layou Adventures - St. Vincent and the Grenadines<br/>
              Version: {waiver.version} | Effective: {new Date(waiver.effective_date).toLocaleDateString()}
            </p>
            <div 
              className="waiver-content prose prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: waiver.content }}
            />
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-red-600 font-bold">Unable to load waiver content. Please contact support.</p>
          </div>
        )}
      </div>

      {/* Signature Form */}
      <form onSubmit={handleSignWaiver} className="bg-gray-700 rounded-xl p-6 border-2 border-gray-600">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
          Electronic Signature
        </h3>

        {/* Agreement Checkbox */}
        <div className="mb-6 bg-gradient-to-r from-gray-600 to-gray-600 p-5 rounded-lg border-2 border-emerald-500/50">
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-1 w-6 h-6 text-emerald-600 border-gray-400 rounded focus:ring-emerald-500 focus:ring-2 cursor-pointer"
            />
            <span className="text-gray-100 text-sm leading-relaxed">
              <strong className="text-white block mb-2 text-base">
                ✓ I agree to the terms and conditions
              </strong>
              I have read and understood the complete liability waiver above. I agree to all terms and conditions and acknowledge that I am signing this agreement voluntarily. By typing my name below, I am providing my electronic signature which has the same legal effect as a handwritten signature.
            </span>
          </label>
        </div>

        {/* Name Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Type your full name to sign *
          </label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            disabled={!agreedToTerms}
            className="w-full px-4 py-4 bg-gray-600 border-2 border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-medium transition-all"
            placeholder="Enter your full name"
          />
          <p className="text-sm text-gray-400 mt-2">
            Must match: <strong className="text-white">{firstParticipant?.full_name}</strong>
          </p>
        </div>

        {/* Warning Box */}
        <div className="mb-6 p-4 bg-yellow-500/20 border border-yellow-500 rounded-lg">
          <p className="text-yellow-200 text-sm">
            <strong>⚠️ Important:</strong> By signing this waiver, you confirm that you understand and accept all risks associated with ATV tours. This is a legally binding agreement.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            className="px-8 py-4 bg-gray-600 hover:bg-gray-500 rounded-lg font-bold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={loading || !agreedToTerms || !fullName}
            className="flex-1 px-8 py-4 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 rounded-lg font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                Processing...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Sign & Continue to Payment
              </>
            )}
          </button>
        </div>

        <p className="text-center text-gray-400 text-xs mt-4">
          By clicking the button above, you confirm that all information is accurate and complete.
        </p>
      </form>

      {/* Help Section */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-400">
          Questions? Contact us at{' '}
          <a href="mailto:info@layouadventures.com" className="text-emerald-400 hover:text-emerald-300 underline">
            info@layouadventures.com
          </a>
          {' '}or{' '}
          <a href="tel:+16143852846" className="text-emerald-400 hover:text-emerald-300 underline">
            (614) 385-2846
          </a>
        </p>
      </div>
    </div>
  );
};

export default WaiverSigning;